import React from 'react';
import { Pressable, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useRouter } from 'expo-router';
import Avatar from '@/components/People/Avatar';
import Unicon from '@/components/General/Unicon';

export const FamilyPositionTrigger = ({ role }) => (
    <View style={styles.roleTrigger} accessibilityLabel={`Family Position: ${role || 'Unassigned'}`}>
        <Text style={styles.roleText}>{role || 'Unassigned'}</Text>
        <Unicon name="chevron-down" size={14} colorVariant="people" />
    </View>
);

const FamilyMemberRow = ({ profile, relationshipInput, onRemove, removeDisabled, isCurrentPerson, isLast, testID }) => {
    const router = useRouter();
    const name = [profile.firstName, profile.lastName].filter(Boolean).join(' ') || 'New Person';

    return (
        <Pressable testID={testID} style={styles.row(isLast)} onLongPress={() => router.push('/people/' + profile.id)}>
            <Avatar disableBorder size={36} profile={profile} />
            <View style={styles.content}>
                <View style={styles.nameRow}>
                    <Text style={styles.name(profile.isArchived)}>{name}</Text>
                    {isCurrentPerson && <Text style={styles.currentPerson}>This person</Text>}
                    {profile.isArchived && <Text style={styles.archived}>Archived</Text>}
                </View>
                <View style={styles.relationship}>{relationshipInput}</View>
            </View>
            <Pressable
                testID={`${testID}-Remove`}
                accessibilityRole="button"
                accessibilityLabel={`Remove ${name} from family`}
                accessibilityState={{ disabled: !!removeDisabled }}
                disabled={removeDisabled}
                onPress={onRemove}
                style={styles.removeButton(removeDisabled)}
            >
                <Unicon name="close-outline" size={20} colorVariant={removeDisabled ? 'textTertiary' : 'textSecondary'} />
            </Pressable>
        </Pressable>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, roundness, typography }) => ({
    row: isLast => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm + spacing.xs,
        paddingVertical: spacing.sm,
        paddingLeft: spacing.md,
        paddingRight: spacing.xs,
        borderBottomWidth: isLast ? 0 : StyleSheet.hairlineWidth,
        borderBottomColor: colors.borderSubtle,
    }),
    content: { flex: 1, minWidth: 0, gap: 2 },
    nameRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: spacing.sm },
    name: archived => ({ fontFamily: fonts.heading, fontSize: typography.body, color: archived ? colors.textTertiary : colors.text, flexShrink: 1 }),
    currentPerson: { fontFamily: fonts.body, fontSize: typography.micro, color: colors.textSecondary },
    archived: { fontFamily: fonts.heading, fontSize: typography.micro, color: colors.warning },
    relationship: { alignSelf: 'flex-start', maxWidth: '100%' },
    roleTrigger: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
        minHeight: 44,
        paddingHorizontal: spacing.sm,
        borderRadius: roundness.sm,
        backgroundColor: colors.peopleContainer,
    },
    roleText: { fontFamily: fonts.heading, fontSize: typography.caption, color: colors.people, flexShrink: 1 },
    removeButton: disabled => ({
        width: 44,
        height: 44,
        borderRadius: roundness.sm,
        alignItems: 'center',
        justifyContent: 'center',
        opacity: disabled ? 0.35 : 1,
        _web: {
            _hover: { backgroundColor: disabled ? 'transparent' : colors.errorContainer },
            '_focus-visible': { outlineColor: colors.people, outlineStyle: 'solid', outlineWidth: 2 },
        },
    }),
}));

export default FamilyMemberRow;
